import { useCallback, useEffect, useRef, useState } from "react"
import type { PdfActiveMetaDataWord } from "@/shared/api/generated"
import { activePlayer } from "@/entities/audio-engine"
import type { PageInfo } from "./types"

interface ActiveWord {
	word: PdfActiveMetaDataWord
	pageNumber: number
	wordIndex: number
}

interface UseActiveWordTrackerResult {
	activeWord: ActiveWord | null
	autoScroll: boolean
	setAutoScroll: (v: boolean) => void
}

const findActiveWord = (
	words: PdfActiveMetaDataWord[],
	time: number
): { word: PdfActiveMetaDataWord; index: number } | null => {
	if (!words || words.length === 0) return null
	let left = 0
	let right = words.length - 1
	while (left <= right) {
		const mid = Math.floor((left + right) / 2)
		const w = words[mid]
		if (time >= w.start_ts && time <= w.end_ts) return { word: w, index: mid }
		if (time < w.start_ts) right = mid - 1
		else left = mid + 1
	}
	return null
}

const SEEK_THRESHOLD = 2

export const useActiveWordTracker = (
	getPageWords: (pageNumber: number) => PdfActiveMetaDataWord[],
	pageInfos: PageInfo[],
	audioPage: number,
	scrollContainerRef: React.RefObject<HTMLDivElement | null>,
	wordElementRefs: React.MutableRefObject<Map<string, HTMLDivElement>>,
	onPageChange?: (page: number) => void,
	onMiss?: (position: number) => void
): UseActiveWordTrackerResult => {
	const [activeWord, setActiveWord] = useState<ActiveWord | null>(null)
	const [autoScroll, setAutoScroll] = useState(true)
	const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
	const missCountRef = useRef(0)
	const lastPositionRef = useRef(0)
	const activeWordRef = useRef<ActiveWord | null>(null)
	const autoScrollRef = useRef(true)
	const wasPlayingRef = useRef(false)

	autoScrollRef.current = autoScroll

	const findCurrentActiveWord = useCallback((): ActiveWord | null => {
		const position = activePlayer.currentPosition
		if (position <= 0) return null
		for (const pageInfo of pageInfos) {
			const words = getPageWords(pageInfo.pageNumber)
			if (words.length === 0) continue
			const result = findActiveWord(words, position)
			if (result) {
				return {
					word: result.word,
					pageNumber: pageInfo.pageNumber,
					wordIndex: result.index,
				}
			}
		}
		return null
	}, [getPageWords, pageInfos])

	// ─── Scroll helper ───────────────────────────────────────

	const doScroll = useCallback(
		(word: ActiveWord, instant: boolean) => {
			if (!autoScrollRef.current || !scrollContainerRef.current) return

			const container = scrollContainerRef.current
			const wordKey = `w-${word.pageNumber}-${word.wordIndex}`
			const wordElement = wordElementRefs.current.get(wordKey)

			// Word not in DOM → scroll to page first
			if (!wordElement) {
				const pageInfo = pageInfos.find(
					p => p.pageNumber === word.pageNumber
				)
				if (pageInfo) {
					container.scrollTo({
						top: pageInfo.yPosition,
						behavior: "instant",
					})
				}
				return
			}

			const viewportHeight = container.clientHeight
			const containerRect = container.getBoundingClientRect()
			const wordRect = wordElement.getBoundingClientRect()
			const wordTopInViewport = wordRect.top - containerRect.top
			const wordBottomInViewport = wordRect.bottom - containerRect.top

			const offScreen =
				wordBottomInViewport < 0 || wordTopInViewport > viewportHeight
			const nearEdge =
				wordTopInViewport < viewportHeight * 0.2 ||
				wordBottomInViewport > viewportHeight * 0.8

			if (instant || offScreen || nearEdge) {
				const wordTopAbsolute =
					wordRect.top - containerRect.top + container.scrollTop
				container.scrollTo({
					top: wordTopAbsolute - viewportHeight * 0.3,
					behavior: instant || offScreen ? "instant" : "smooth",
				})
			}
		},
		[scrollContainerRef, wordElementRefs, pageInfos]
	)

	// ─── Main poll loop ──────────────────────────────────────

	useEffect(() => {
		const check = () => {
			const currentPos = activePlayer.currentPosition
			const isPlaying = activePlayer.isPlaying
			const justResumed = !wasPlayingRef.current && isPlaying
			wasPlayingRef.current = isPlaying

			// Detect seek
			const jumped =
				Math.abs(currentPos - lastPositionRef.current) > SEEK_THRESHOLD
			lastPositionRef.current = currentPos

			if (jumped) {
				missCountRef.current = 0
				activeWordRef.current = null
				setActiveWord(null)
				// Don't return — fall through to try finding the word at new position
			}

			const found = findCurrentActiveWord()

			if (found) {
				missCountRef.current = 0

				if (found.pageNumber !== audioPage && onPageChange) {
					onPageChange(found.pageNumber)
				}

				const changed =
					!activeWordRef.current ||
					activeWordRef.current.pageNumber !== found.pageNumber ||
					activeWordRef.current.wordIndex !== found.wordIndex

				activeWordRef.current = found
				if (changed) setActiveWord(found)

				// Scroll when: word changed, just resumed, or just seeked
				if (changed || justResumed || jumped) {
					doScroll(found, justResumed || jumped)
				} else if (autoScrollRef.current && isPlaying) {
					// Periodic check: even if word didn't change, ensure it's visible
					// (covers case where user scrolled away while playing)
					doScroll(found, false)
				}
			} else {
				missCountRef.current++

				if (missCountRef.current >= 3 && onMiss) {
					const pos = activePlayer.currentPosition
					if (pos > 0) {
						missCountRef.current = 0
						onMiss(pos)
					}
				}

				if (
					activeWordRef.current &&
					Math.abs(audioPage - activeWordRef.current.pageNumber) > 1
				) {
					activeWordRef.current = null
					setActiveWord(null)
				}
			}
		}

		check()
		intervalRef.current = setInterval(check, 250)

		return () => {
			if (intervalRef.current) {
				clearInterval(intervalRef.current)
				intervalRef.current = null
			}
		}
	}, [findCurrentActiveWord, audioPage, onPageChange, onMiss, doScroll])

	// ─── Scroll to page when no word yet (big seek / initial load) ──

	useEffect(() => {
		if (!autoScroll || audioPage <= 0 || activeWord) return
		if (!scrollContainerRef.current || pageInfos.length === 0) return

		const pageInfo = pageInfos.find(p => p.pageNumber === audioPage)
		if (!pageInfo) return

		scrollContainerRef.current.scrollTo({
			top: pageInfo.yPosition,
			behavior: "instant",
		})
	}, [autoScroll, audioPage, activeWord, pageInfos, scrollContainerRef])

	return { activeWord, autoScroll, setAutoScroll }
}
